import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { addPA } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaCreditCard, 
  FaFileAlt, 
  FaDollarSign, 
  FaUsers, 
  FaCalendarAlt, 
  FaSave,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa'
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap'
import './AddPlan.css'

// eslint-disable-next-line
export default function (props) {
  const navigate = useNavigate()
  const { authenticatedUser, setAlert } = useUserContext()

  const [planName, setPlanName] = useState('')
  const [price, setPrice] = useState('')
  const [maxGuests, setMaxGuests] = useState('')
  const [months, setMonths] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const handleChangePlanName = (e) => {
    setPlanName(e.target.value)
    setErrors(prev => ({
      ...prev,
      planName: e.target.value.trim() === '' ? 'El nombre del plan es requerido' : ''
    }))
  }

  const handleChangePrice = (e) => {
    const value = e.target.value
    setPrice(value)
    setErrors(prev => ({
      ...prev,
      price: value === '' ? 'El precio es requerido' : 
             parseFloat(value) <= 0 ? 'El precio debe ser mayor a 0' : ''
    }))
  }

  const handleChangeMaxGuests = (e) => {
    const value = e.target.value
    setMaxGuests(value)
    setErrors(prev => ({
      ...prev,
      maxGuests: value === '' ? 'El número máximo de invitados es requerido' : 
                 parseInt(value) <= 0 ? 'El número de invitados debe ser mayor a 0' : ''
    }))
  }

  const handleChangeMonths = (e) => {
    const value = e.target.value
    setMonths(value)
    setErrors(prev => ({
      ...prev,
      months: value === '' ? 'El número de meses es requerido' : 
              parseInt(value) <= 0 ? 'El número de meses debe ser mayor a 0' : ''
    }))
  }

  const handleChangeDescription = (e) => {
    setDescription(e.target.value)
    setErrors(prev => ({
      ...prev,
      description: ''
    }))
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (planName.trim() === '') {
      newErrors.planName = 'El nombre del plan es requerido'
    }
    
    if (price === '' || parseFloat(price) <= 0) {
      newErrors.price = 'El precio debe ser mayor a 0'
    }
    
    if (maxGuests === '' || parseInt(maxGuests) <= 0) {
      newErrors.maxGuests = 'El número máximo de invitados debe ser mayor a 0'
    }
    
    if (months === '' || parseInt(months) <= 0) {
      newErrors.months = 'El número de meses debe ser mayor a 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      let planData = await addPA({ 
        path: 'plans',
        data: {
          planName: planName.trim(),
          price: parseFloat(price),
          maxGuests: parseInt(maxGuests),
          months: parseInt(months),
          description: description.trim() || '',
        }, 
        token: authenticatedUser.value 
      })

      if(Object.prototype.hasOwnProperty.call(planData, 'planId')) {
        setAlert({
          message: 'Plan creado exitosamente',
          msgType: 'success'
        })
        navigate('/planes')
      } else {
        setAlert({
          message: planData.message || 'Error al crear el plan',
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al crear el plan. Por favor, inténtalo de nuevo.',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }

  window.scrollTo({
    top: 0,
    behavior: 'auto',
  })

  return (
    <div className="add-plan-container">
      <div className="container">
        <Row className="justify-content-center">
          <Col lg={8} md={10} sm={12}>
            {/* Header */}
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <FaCreditCard />
                </div>
                <div className="header-text">
                  <h1>Crear Nuevo Plan</h1>
                  <p>Completa la información para registrar un nuevo plan de suscripción</p>
                </div>
              </div>
            </div>

            {/* Form Card */}
            <Card className="form-card">
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  {/* Plan Name */}
                  <div className="form-group">
                    <label className="form-label">
                      <FaFileAlt className="field-icon" />
                      Nombre del Plan
                      <span className="required-field">*</span>
                    </label>
                    <Form.Control
                      type="text"
                      placeholder="Ej: Plan Básico, Plan Premium..."
                      value={planName}
                      onChange={handleChangePlanName}
                      className={`form-control-custom ${errors.planName ? 'is-invalid' : ''}`}
                    />
                    {errors.planName && (
                      <div className="error-message">
                        <FaExclamationTriangle className="error-icon" />
                        {errors.planName}
                      </div>
                    )}
                  </div>

                  {/* Price and Max Guests Row */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <FaDollarSign className="field-icon" />
                        Precio
                        <span className="required-field">*</span>
                      </label>
                      <div className="price-input-group">
                        <span className="currency-symbol">$</span>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={price}
                          onChange={handleChangePrice}
                          className={`form-control-custom ${errors.price ? 'is-invalid' : ''}`}
                        />
                      </div>
                      {errors.price && (
                        <div className="error-message">
                          <FaExclamationTriangle className="error-icon" />
                          {errors.price}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FaUsers className="field-icon" />
                        Máximo de Invitados
                        <span className="required-field">*</span>
                      </label>
                      <div className="number-input-group">
                        <Form.Control
                          type="number"
                          min="1"
                          placeholder="0"
                          value={maxGuests}
                          onChange={handleChangeMaxGuests}
                          className={`form-control-custom ${errors.maxGuests ? 'is-invalid' : ''}`}
                        />
                        <span className="unit-label">invitados</span>
                      </div>
                      {errors.maxGuests && (
                        <div className="error-message">
                          <FaExclamationTriangle className="error-icon" />
                          {errors.maxGuests}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Months and Description Row */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <FaCalendarAlt className="field-icon" />
                        Duración
                        <span className="required-field">*</span>
                      </label>
                      <div className="number-input-group">
                        <Form.Control
                          type="number"
                          min="1"
                          placeholder="0"
                          value={months}
                          onChange={handleChangeMonths}
                          className={`form-control-custom ${errors.months ? 'is-invalid' : ''}`}
                        />
                        <span className="unit-label">meses</span>
                      </div>
                      {errors.months && (
                        <div className="error-message">
                          <FaExclamationTriangle className="error-icon" />
                          {errors.months}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        <FaFileAlt className="field-icon" />
                        Descripción
                      </label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Describe las características del plan..."
                        value={description}
                        onChange={handleChangeDescription}
                        className="form-control-custom description-textarea"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="form-actions">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="cancel-button"
                      onClick={() => navigate('/planes')}
                      disabled={isLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="save-button"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creando Plan...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Crear Plan
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
